<template>
  <div @click="closeModal"
       class="fixed z-50 inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
    <div @click.stop class="relative bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
      <!-- Modal header -->
      <div class="flex items-center justify-between p-4 border-b">
        <h3 class="text-xl font-semibold text-gray-900">
          Add New Session
        </h3>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-500">
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Modal body -->
      <div class="p-6">
        <form @submit.prevent="submitForm">
          <div class="mb-4">
            <label for="sessionDate" class="g--label mb-1">
              Date
            </label>
            <VueDatePicker :format="format" :is-24="!timezone.includes('America')" :flow="flow"
                           v-model="formData.date"></VueDatePicker>
            <p class="mt-2 text-sm text-gray-500 mb-4">Your timezone is {{ timezone }}</p>
          </div>


          <div class="mb-6">
            <label class="g--label mb-1">
              Duration
            </label>
            <div class="grid grid-cols-3 gap-2">
              <button v-for="duration in durations"
                      :key="duration.minutes"
                      type="button"
                      @click="formData.duration = duration.minutes"
                      :class="['px-3 py-2 text-sm rounded-md',
                                            formData.duration === duration.minutes ? 
                                            'bg-teal text-white' :
                                            'bg-gray-100 hover:bg-gray-200 text-gray-700']">
                {{ duration.label }}
              </button>
            </div>
          </div>

          <div v-if="missingDuration" class="text-red mb-2 text-sm">
            <p>Please select a duration for your session.</p>
          </div>

          <div class="flex justify-end space-x-3">
            <button type="button"
                    @click="closeModal"
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">
              Cancel
            </button>
            <button type="submit"
                    class="g--button">
              Save Session
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from 'moment';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'

import { ref, onMounted } from 'vue';

const flow = ref(['calendar', 'time']);


const durations = ref([
  {minutes: 10, label: '10 minutes'},
  {minutes: 15, label: '15 minutes'},
  {minutes: 20, label: '20 minutes'},
  {minutes: 30, label: '30 minutes'},
  {minutes: 40, label: '40 minutes'},
  {minutes: 45, label: '45 minutes'},
  {minutes: 60, label: '1 hour'},
  {minutes: 75, label: '1 hour 15 min'},
  {minutes: 90, label: '1 hour 30 min'},
  {minutes: 120, label: '2 hours'},
  {minutes: 150, label: '2 hours 30 min'}
]);


const timezone = ref(window.timezone);

const format = ref(timezone.value.includes('America') ? 'EEEE, MMMM d yyyy - h:mm a' : 'EEEE, d MMMM yyyy - HH:mm');

const formData = ref({
  date: '',
  startTime: '',
  duration: ''
});

const missingDuration = ref(false);

const props = defineProps({
  classItem: {
    type: Object,
    required: true
  },
  sessionId: {
    type: Number,
    required: false
  },
  startTime: {
    type: String,
    required: false
  },
  duration: {
    type: Number,
    required: false
  }
});
const emit = defineEmits(['session-added']);

const closeModal = () => {
  emit('session-added');
  resetForm();
};

onMounted(() => {
  console.log(props.classItem);
  console.log(props.startTime);
  if (props.startTime) {
    console.log('start');
    formData.value.date = new Date(props.startTime);
    formData.value.duration = props.duration;
  } else {
    formData.value.date = new Date(new Date().setDate(new Date().getDate() + 1)).setHours(9, 0, 0, 0);
  }
});

const submitForm = () => {

  // check if formData.duration is set
  if (!formData.value.duration || formData.value.duration <= 0) {
    missingDuration.value = true;
    return;
  } else {
    missingDuration.value = false;
  }

  // Combine date and time into start datetime
  const startDateTime = moment(formData.value.date);

  // Calculate end datetime by adding duration
  const endDateTime = moment(startDateTime).add(formData.value.duration, 'minutes');

  const sessionData = {
    start_time: startDateTime.format('YYYY-MM-DD HH:mm:ss'),
    end_time: endDateTime.format('YYYY-MM-DD HH:mm:ss')
  };

  saveSession(sessionData);

};

const saveSession = async (sessionData) => {
  // Save the class data

  try {
    let data = {
      sectionId: 2,
      enabled: true,
      fields: {
        class: [props.classItem.id],
        startTime: {
          datetime: sessionData.start_time,
          timezone: timezone.value
        },
        endTime: {
          datetime: sessionData.end_time,
          timezone: timezone.value
        }
      }
    };
    if (props.sessionId) {
      data.entryId = props.sessionId;
    }
    data[csrfTokenName] = csrfTokenValue;

    const response = await fetch('/actions/entries/save-entry', {
      method: 'POST',
      headers: {
        'Content-type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save entry');
    }

    const savedEntry = await response.json();

    // Optional: Emit the saved entry data
    emit('session-added', sessionData);
    resetForm();
    closeModal();

  } catch (error) {
    console.error('Error saving entry:', error);
  }
};


const resetForm = () => {
  formData.value = {
    date: '',
    startTime: '',
    duration: ''
  };
};
</script>
